import type { Prisma, PrismaClient, RecurringTemplate, Renewal } from '@prisma/client';
import { z } from 'zod';
import { audit } from '../../core/audit.js';
import { HttpError } from '../../core/errors.js';
import { cancelJobs, enqueueJob, registerJobHandler } from '../../core/jobs.js';
import { loadAuthContext } from '../auth/auth.middleware.js';
import { queueEmail } from '../messaging/messaging.service.js';
import {
  addDays,
  approveDocument,
  assertWithinLimit,
  documentInputSchema,
  isoOf,
  saveDraft,
  toDate,
  type DocumentInput,
} from '../sales/documents.service.js';
import { permissionsGranted } from '../auth/auth.middleware.js';
import { sendDocument, sendPreview } from '../sales/send.service.js';
import type { Config } from '../../core/config.js';

type Db = PrismaClient | Prisma.TransactionClient;
const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Use YYYY-MM-DD');

// ───────────────────────── recurring invoices ─────────────────────────

export const templateInputSchema = z
  .object({
    name: z.string().trim().min(2).max(150),
    partyId: z.number().int().positive(),
    currency: z
      .string()
      .trim()
      .length(3)
      .transform((v) => v.toUpperCase())
      .default('UGX'),
    frequency: z.enum(['monthly', 'quarterly', 'yearly', 'custom']),
    everyDays: z.number().int().min(1).max(730).nullable().optional(),
    startDate: isoDate,
    endDate: isoDate.nullable().optional(),
    mode: z.enum(['draft', 'approve', 'approve_send']).default('draft'),
    paymentTermsDays: z.number().int().min(0).max(365).default(14),
    reference: z.string().trim().max(100).nullable().optional(),
    notes: z.string().trim().max(4000).nullable().optional(),
    lines: documentInputSchema.shape.lines,
  })
  .refine((v) => v.frequency !== 'custom' || !!v.everyDays, {
    message: 'Enter how many days between invoices',
    path: ['everyDays'],
  })
  .refine((v) => !v.endDate || v.endDate >= v.startDate, {
    message: 'The end date is before the start date',
    path: ['endDate'],
  });
export type TemplateInput = z.infer<typeof templateInputSchema>;

/** Adds calendar months but keeps the day-of-month the schedule started on (31 Jan -> 28 Feb -> 31 Mar). */
export function addMonthsAnchored(d: Date, months: number, anchorDay: number): Date {
  const y = d.getUTCFullYear();
  const m = d.getUTCMonth() + months;
  const last = new Date(Date.UTC(y, m + 1, 0)).getUTCDate();
  return new Date(Date.UTC(y, m, Math.min(anchorDay, last)));
}

export function nextRunAfter(
  current: Date,
  t: Pick<RecurringTemplate, 'frequency' | 'everyDays' | 'startDate'>,
): Date {
  const anchor = t.startDate.getUTCDate();
  switch (t.frequency) {
    case 'monthly':
      return addMonthsAnchored(current, 1, anchor);
    case 'quarterly':
      return addMonthsAnchored(current, 3, anchor);
    case 'yearly':
      return addMonthsAnchored(current, 12, anchor);
    default:
      return addDays(current, t.everyDays ?? 30);
  }
}

export const templateView = (t: RecurringTemplate, partyName?: string) => ({
  id: t.id,
  name: t.name,
  partyId: t.partyId,
  partyName,
  currency: t.currency,
  frequency: t.frequency,
  everyDays: t.everyDays,
  startDate: isoOf(t.startDate),
  endDate: isoOf(t.endDate),
  nextRunDate: isoOf(t.nextRunDate),
  mode: t.mode,
  paymentTermsDays: t.paymentTermsDays,
  reference: t.reference,
  notes: t.notes,
  lines: t.lines,
  status: t.status,
  lastRunAt: t.lastRunAt,
  runCount: t.runCount,
});

/** Preview of the next few invoice dates for the screen. */
export function upcomingDates(t: RecurringTemplate, count = 4): string[] {
  const out: string[] = [];
  let d = t.nextRunDate;
  for (let i = 0; i < count; i += 1) {
    if (t.endDate && d > t.endDate) break;
    out.push(isoOf(d)!);
    d = nextRunAfter(d, t);
  }
  return out;
}

export async function saveTemplate(
  db: Db,
  args: { companyId: number; userId: number; input: TemplateInput; id?: number },
): Promise<RecurringTemplate> {
  const { input } = args;
  const party = await db.party.findFirst({
    where: { id: input.partyId, companyId: args.companyId, active: true },
  });
  if (!party || !party.isClient) throw new HttpError(400, 'VALIDATION_ERROR', 'Choose a client');
  const data = {
    name: input.name,
    partyId: party.id,
    currency: input.currency,
    frequency: input.frequency,
    everyDays: input.frequency === 'custom' ? input.everyDays! : null,
    startDate: toDate(input.startDate),
    endDate: input.endDate ? toDate(input.endDate) : null,
    mode: input.mode,
    paymentTermsDays: input.paymentTermsDays,
    reference: input.reference ?? null,
    notes: input.notes ?? null,
    lines: input.lines as unknown as Prisma.InputJsonValue,
  };
  if (args.id) {
    const cur = await db.recurringTemplate.findFirst({
      where: { id: args.id, companyId: args.companyId },
    });
    if (!cur) throw new HttpError(404, 'NOT_FOUND', 'Template not found');
    // Editing the start date of a schedule that has not run yet re-bases it.
    const rebase = cur.runCount === 0 ? { nextRunDate: data.startDate } : {};
    return db.recurringTemplate.update({ where: { id: cur.id }, data: { ...data, ...rebase } });
  }
  return db.recurringTemplate.create({
    data: { ...data, companyId: args.companyId, ownerId: args.userId, nextRunDate: data.startDate },
  });
}

export interface RunResult {
  templateId: number;
  invoiceId: number | null;
  status: 'created' | 'skipped' | 'failed';
  note?: string;
}

/** Creates the invoice due on `runDate` for one template. Idempotent per (template, date). */
async function generateOne(
  db: PrismaClient,
  config: Pick<Config, 'APP_URL'>,
  t: RecurringTemplate,
  runDate: Date,
): Promise<RunResult> {
  const auth = await loadAuthContext(db, t.ownerId, t.companyId);
  if (!auth)
    return {
      templateId: t.id,
      invoiceId: null,
      status: 'failed',
      note: 'Template owner is no longer active',
    };
  // Claim the date first; a second sweep (or a second server) hits the unique key and stops here.
  try {
    await db.recurringRun.create({ data: { templateId: t.id, runDate, status: 'created' } });
  } catch {
    return { templateId: t.id, invoiceId: null, status: 'skipped', note: 'Already generated' };
  }
  const run = await db.recurringRun.findUniqueOrThrow({
    where: { templateId_runDate: { templateId: t.id, runDate } },
  });
  try {
    const input: DocumentInput = {
      ...documentInputSchema.parse({
        partyId: t.partyId,
        currency: t.currency,
        issueDate: isoOf(runDate),
        dueDate: isoOf(addDays(runDate, t.paymentTermsDays)),
        reference: t.reference,
        notes: t.notes,
        lines: t.lines,
      }),
    };
    const invoice = await db.$transaction(async (tx) => {
      const draft = await saveDraft(tx, { auth, kind: 'invoice', input });
      let final = draft;
      const mayApprove =
        t.mode !== 'draft' && permissionsGranted(auth.permissions, 'sales.invoice.approve');
      if (mayApprove) {
        try {
          assertWithinLimit(auth, draft.baseTotal);
          final = await approveDocument(tx, auth, draft.id);
        } catch {
          /* above the owner's limit: stays a draft for someone else to approve */
        }
      }
      await audit(
        tx,
        { companyId: t.companyId, userId: t.ownerId, ip: null },
        {
          action: 'recurring.generated',
          entity: 'sales_document',
          entityId: final.id,
          after: { templateId: t.id, runDate: isoOf(runDate), status: final.status },
        },
      );
      return final;
    });
    await db.recurringRun.update({ where: { id: run.id }, data: { invoiceId: invoice.id } });
    if (t.mode === 'approve_send' && invoice.status === 'approved') {
      try {
        const pv = await sendPreview(db, auth, 'invoice', invoice.id);
        if (pv.defaultEmails.length)
          await sendDocument(db, config, auth, 'invoice', invoice.id, {
            channels: ['email'],
            includeLink: true,
            email: {
              to: pv.defaultEmails,
              cc: [],
              bcc: [],
              subject: pv.email.subject,
              message: pv.email.body,
              attachPdf: true,
            },
          });
      } catch {
        /* the invoice exists; the owner is told below that it was not sent */
      }
    }
    await db.notification.create({
      data: {
        companyId: t.companyId,
        userId: t.ownerId,
        title: `Recurring invoice ${invoice.number ?? '(draft)'} created for ${t.name}${invoice.status === 'draft' ? ': needs approval' : ''}`,
        link: `/sales/invoices/${invoice.id}`,
      },
    });
    return { templateId: t.id, invoiceId: invoice.id, status: 'created' };
  } catch (e) {
    const note = e instanceof Error ? e.message.slice(0, 400) : 'Failed';
    await db.recurringRun.update({ where: { id: run.id }, data: { status: 'failed', note } });
    await db.recurringTemplate.update({ where: { id: t.id }, data: { status: 'paused' } });
    await db.notification.create({
      data: {
        companyId: t.companyId,
        userId: t.ownerId,
        title: `Recurring invoice for ${t.name} failed and was paused: ${note}`,
        link: '/sales/recurring',
      },
    });
    return { templateId: t.id, invoiceId: null, status: 'failed', note };
  }
}

/** Runs every schedule that is due (catching up at most 3 missed periods per template per pass). */
export async function runDueRecurring(
  db: PrismaClient,
  config: Pick<Config, 'APP_URL'>,
  now = new Date(),
  onlyTemplateId?: number,
): Promise<RunResult[]> {
  const today = toDate(now.toISOString().slice(0, 10));
  const due = await db.recurringTemplate.findMany({
    where: {
      status: 'active',
      nextRunDate: { lte: today },
      ...(onlyTemplateId ? { id: onlyTemplateId } : {}),
    },
    take: 100,
  });
  const results: RunResult[] = [];
  for (const t0 of due) {
    let t = t0;
    for (let i = 0; i < 3 && t.status === 'active' && t.nextRunDate <= today; i += 1) {
      if (t.endDate && t.nextRunDate > t.endDate) {
        t = await db.recurringTemplate.update({ where: { id: t.id }, data: { status: 'ended' } });
        break;
      }
      const r = await generateOne(db, config, t, t.nextRunDate);
      results.push(r);
      if (r.status === 'failed') break;
      const next = nextRunAfter(t.nextRunDate, t);
      t = await db.recurringTemplate.update({
        where: { id: t.id },
        data: {
          nextRunDate: next,
          lastRunAt: new Date(),
          runCount: { increment: r.status === 'created' ? 1 : 0 },
          ...(t.endDate && next > t.endDate ? { status: 'ended' } : {}),
        },
      });
    }
  }
  return results;
}

// ───────────────────────── renewals ─────────────────────────

export const SERVICES = ['domain', 'hosting', 'ssl', 'maintenance', 'other'] as const;
export const renewalInputSchema = z.object({
  partyId: z.number().int().positive(),
  service: z.enum(SERVICES),
  identifier: z.string().trim().min(2).max(200),
  startDate: isoDate,
  expiryDate: isoDate,
  termMonths: z.number().int().min(1).max(120).default(12),
  price: z
    .union([z.string(), z.number()])
    .transform((v) => String(v).trim())
    .refine((v) => /^\d{1,15}(\.\d{1,4})?$/.test(v), 'Enter a valid price')
    .default('0'),
  currency: z
    .string()
    .trim()
    .length(3)
    .transform((v) => v.toUpperCase())
    .default('UGX'),
  autoInvoice: z.boolean().default(false),
  notifyClient: z.boolean().default(false),
  notes: z.string().trim().max(500).nullable().optional(),
  ownerId: z.number().int().positive().nullable().optional(),
});
export type RenewalInput = z.infer<typeof renewalInputSchema>;

export const ALERT_DAYS = [60, 30, 14, 7];

export async function ensureRenewalRules(db: Db, companyId: number) {
  const existing = await db.reminderRule.count({ where: { companyId, docType: 'renewal' } });
  if (existing > 0) return;
  for (const d of ALERT_DAYS)
    await db.reminderRule.create({
      data: {
        companyId,
        docType: 'renewal',
        name: `Renewal alert ${d} days before expiry`,
        trigger: 'before_expiry',
        offsetDays: d,
        audience: 'both',
        templateName: 'renewal',
        active: true,
      },
    });
}

export const renewalView = (
  r: Renewal,
  partyName?: string,
  today = new Date().toISOString().slice(0, 10),
) => {
  const expiry = isoOf(r.expiryDate)!;
  const days = Math.round(
    (Date.parse(`${expiry}T00:00:00Z`) - Date.parse(`${today}T00:00:00Z`)) / 86_400_000,
  );
  return {
    id: r.id,
    partyId: r.partyId,
    partyName,
    service: r.service,
    identifier: r.identifier,
    startDate: isoOf(r.startDate),
    expiryDate: expiry,
    daysLeft: days,
    displayStatus: r.status === 'active' && days < 0 ? 'expired' : r.status,
    termMonths: r.termMonths,
    price: r.price.toFixed(4),
    currency: r.currency,
    autoInvoice: r.autoInvoice,
    notifyClient: r.notifyClient,
    status: r.status,
    lastInvoiceId: r.lastInvoiceId,
    notes: r.notes,
    ownerId: r.ownerId,
  };
};

const alertKey = (id: number) => `rem:renewal:${id}:`;

/** Alerts at 60/30/14/7 days before expiry as idempotent jobs (one per rule and expiry date). */
export async function scheduleRenewalAlerts(
  db: PrismaClient,
  r: Renewal,
  now = new Date(),
): Promise<number> {
  await cancelJobs(db, alertKey(r.id));
  await db.scheduledReminder.updateMany({
    where: { docType: 'renewal', docId: r.id, status: 'pending' },
    data: { status: 'cancelled' },
  });
  if (r.status !== 'active') return 0;
  await ensureRenewalRules(db, r.companyId);
  const rules = await db.reminderRule.findMany({
    where: { companyId: r.companyId, docType: 'renewal', active: true },
  });
  let n = 0;
  for (const rule of rules) {
    const d = addDays(r.expiryDate, -rule.offsetDays);
    const runAt = new Date(
      Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate(), 8 - 3, 0, 0),
    );
    if (runAt.getTime() < now.getTime() - 86_400_000) continue; // never burst old alerts
    const row = await db.scheduledReminder.upsert({
      where: { docId_ruleId: { docId: r.id, ruleId: rule.id } },
      create: { companyId: r.companyId, ruleId: rule.id, docType: 'renewal', docId: r.id, runAt },
      update: { status: 'pending', runAt, firedAt: null, note: null },
    });
    const key = `${alertKey(r.id)}${rule.id}:${isoOf(r.expiryDate)}`;
    await db.job.deleteMany({ where: { idempotencyKey: key } });
    await enqueueJob(db, {
      companyId: r.companyId,
      type: 'renewal.alert',
      payload: {
        scheduledId: row.id,
        renewalId: r.id,
        expiry: isoOf(r.expiryDate),
        days: rule.offsetDays,
        audience: rule.audience,
      },
      runAt,
      idempotencyKey: key,
    });
    n += 1;
  }
  return n;
}

/** Draft renewal invoice for the next term (linked back to the renewal). */
export async function createRenewalInvoice(
  db: PrismaClient,
  renewal: Renewal,
  auth: Parameters<typeof saveDraft>[1]['auth'],
) {
  const party = await db.party.findUniqueOrThrow({ where: { id: renewal.partyId } });
  const term =
    renewal.termMonths % 12 === 0
      ? `${renewal.termMonths / 12} year${renewal.termMonths === 12 ? '' : 's'}`
      : `${renewal.termMonths} months`;
  const revenueKey =
    renewal.service === 'domain' || renewal.service === 'hosting' || renewal.service === 'ssl'
      ? 'REV_HOSTING'
      : 'REV_CONSULT';
  const next = addMonthsAnchored(
    renewal.expiryDate,
    renewal.termMonths,
    renewal.expiryDate.getUTCDate(),
  );
  const invoice = await db.$transaction((tx) =>
    saveDraft(tx, {
      auth,
      kind: 'invoice',
      input: documentInputSchema.parse({
        partyId: party.id,
        currency: renewal.currency,
        issueDate: new Date().toISOString().slice(0, 10),
        reference: renewal.identifier,
        notes: `Renewal until ${isoOf(next)}`,
        lines: [
          {
            description: `Renewal: ${renewal.service} ${renewal.identifier} (${term}, until ${isoOf(next)})`,
            quantity: '1',
            unitPrice: renewal.price.toString(),
            vatCode: 'VAT_STD',
            revenueAccountKey: revenueKey,
          },
        ],
      }),
    }),
  );
  await db.renewal.update({ where: { id: renewal.id }, data: { lastInvoiceId: invoice.id } });
  return invoice;
}

export function registerAutomationJobs() {
  registerJobHandler('renewal.alert', async (job, ctx) => {
    const p = job.payload as {
      scheduledId: number;
      renewalId: number;
      expiry: string;
      days: number;
      audience: string;
    };
    const db = ctx.db;
    const row = await db.scheduledReminder.findUnique({ where: { id: p.scheduledId } });
    if (!row || row.status !== 'pending') return;
    const r = await db.renewal.findUnique({ where: { id: p.renewalId } });
    // Re-checked when it fires: still active, and the expiry date is the one this alert was set for.
    if (!r || r.status !== 'active' || isoOf(r.expiryDate) !== p.expiry) {
      await db.scheduledReminder.update({
        where: { id: row.id },
        data: { status: 'skipped', firedAt: ctx.now, note: 'Renewed, cancelled or date changed' },
      });
      return;
    }
    const party = await db.party.findUniqueOrThrow({
      where: { id: r.partyId },
      include: { contacts: true },
    });
    const ownerId =
      r.ownerId ??
      party.ownerId ??
      (
        await db.user.findFirst({
          where: {
            companyId: r.companyId,
            status: 'active',
            roles: { some: { role: { name: 'Owner' } } },
          },
          select: { id: true },
        })
      )?.id;
    if (ownerId && p.audience !== 'client') {
      await db.task.create({
        data: {
          companyId: r.companyId,
          assigneeId: ownerId,
          title: `${r.service} ${r.identifier} for ${party.name} expires in ${p.days} days`,
          description: `Renewal price ${r.currency} ${r.price.toFixed(0)}`,
          dueDate: addDays(r.expiryDate, -p.days),
          relatedType: 'renewal',
          relatedId: r.id,
          source: 'reminder',
        },
      });
      await db.notification.create({
        data: {
          companyId: r.companyId,
          userId: ownerId,
          title: `${r.identifier} (${party.name}) expires in ${p.days} days`,
          link: '/sales/recurring',
        },
      });
    }
    if (r.notifyClient && p.audience !== 'owner') {
      const to = party.contacts.filter((c) => c.receivesDocuments && c.email).map((c) => c.email!);
      if (to.length) {
        const company = await db.company.findUniqueOrThrow({ where: { id: r.companyId } });
        await queueEmail(db, {
          companyId: r.companyId,
          to,
          subject: `Your ${r.service} ${r.identifier} renews soon`,
          text: `Dear ${party.name},

Your ${r.service} ${r.identifier} expires on ${p.expiry.split('-').reverse().join('/')} (${p.days} days from now). Reply to this email and we will renew it for you.

Kind regards,
${company.name}`,
          docType: 'renewal',
          docId: r.id,
          templateName: 'renewal',
        }).catch(() => undefined);
      }
    }
    // Optional automation: prepare the renewal invoice draft 30 days out.
    if (r.autoInvoice && p.days <= 30 && !r.lastInvoiceId && ownerId) {
      const auth = await loadAuthContext(db, ownerId, r.companyId);
      if (auth && permissionsGranted(auth.permissions, 'sales.invoice.create')) {
        const inv = await createRenewalInvoice(db, r, auth).catch(() => null);
        if (inv)
          await db.notification.create({
            data: {
              companyId: r.companyId,
              userId: ownerId,
              title: `Renewal invoice drafted for ${r.identifier}`,
              link: `/sales/invoices/${inv.id}`,
            },
          });
      }
    }
    await db.scheduledReminder.update({
      where: { id: row.id },
      data: { status: 'fired', firedAt: ctx.now },
    });
  });
}

/** Safety net for the cron sweep: any active renewal without pending alerts gets them scheduled. */
export async function scheduleMissingRenewalAlerts(db: PrismaClient): Promise<number> {
  const rows = await db.renewal.findMany({
    where: { status: 'active', expiryDate: { gte: toDate(new Date().toISOString().slice(0, 10)) } },
    take: 500,
  });
  let n = 0;
  for (const r of rows) {
    const any = await db.scheduledReminder.count({ where: { docType: 'renewal', docId: r.id } });
    if (any === 0) n += await scheduleRenewalAlerts(db, r);
  }
  return n;
}
